import useAdBlockStore from "store/AdBlockStore";
import { Typography, Card, Badge, Empty, Space } from "antd";
import useKeypress from "react-use-keypress";
import { useState } from "react";

const { Title, Text } = Typography;

const styles = {
  panel: {
    border: '1px solid transparent',
  },
  panelBody: {
    padding: '8px',
  },
  title: {
    color: '#ffffff',
    marginBottom: '16px',
    fontSize: '14px',
    fontWeight: '600'
  },
  suggestionCard: {
    backgroundColor: '#2a2a2a',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  suggestionCardBody: {
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
  },
  badge: {
    backgroundColor: '#595959',
    color: '#ffffff',
    fontSize: '10px',
    minWidth: '20px',
    height: '20px',
    lineHeight: '20px',
    marginRight: '12px'
  },
  itemText: {
    color: '#ffffff',
    fontWeight: '500',
    fontSize: '13px'
  },
  emptyText: {
    color: '#8c8c8c',
    fontSize: '13px'
  },
  emptyContainer: {
    margin: '24px 0'
  },
  noSuggestionsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: '24px'
  }
};

const SuggestionItem = ({ index, text, onClick }) => (
  <Card
    size="small"
    className="suggestion-item"
    hoverable
    style={styles.suggestionCard}
    styles={{ body: styles.suggestionCardBody }}
    onClick={onClick}
  >
    <Badge count={index + 1} style={styles.badge} />
    <Text style={styles.itemText}>{text}</Text>
  </Card>
);

const EmptyState = ({ text }) => (
  <Empty
    description={<Text style={styles.emptyText}>{text}</Text>}
    image={Empty.PRESENTED_IMAGE_SIMPLE}
    style={styles.emptyContainer}
  />
);

const SuggestionsPanel = () => {
  const [manualEntryMode, setManualEntryMode] = useState(true);
  const currentSuggestions = useAdBlockStore((state) => state.currentSuggestions);
  const activeField = useAdBlockStore((state) => state.activeField);
  const updateCurrentProduct = useAdBlockStore((state) => state.updateCurrentProduct);
  const focusNextField = useAdBlockStore((state) => state.focusNextField);
  const products = useAdBlockStore((state) => state.products);
  const activeBlockData = useAdBlockStore(state => state.activeBlockData);
  
  function handleTabNext(event) {
    event.preventDefault();
    const focusableElements = document.getElementById("product-builder")?.querySelectorAll(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([disabled]), [role="checkbox"]:not([disabled])'
    );
    const elementsArray = Array.from(focusableElements || []);
    const currentIndex = elementsArray.indexOf(document.activeElement);
    const nextElement = elementsArray[currentIndex + 1] || elementsArray[0];
    nextElement?.focus();
  }

  useKeypress(["1", "2", "3", "4", "5", "6", "7", "8", "9"], (event) => {
    if (manualEntryMode) return;
    
    const config = getConfig();
    const selectedIndex = parseInt(event.key) - 1;
    
    if (config && selectedIndex < config.items.length) {
      event.preventDefault();
      const selectedItem = config.items[selectedIndex];
      
      if (config.onSelect) {
        config.onSelect(selectedItem);
      }
      setTimeout(() => focusNextField("product-builder"), 0);
    }
  });

  useKeypress(["0"], (event) => {
    console.log("Entering manual entry mode");
    // to allow 0 in parent
    if(manualEntryMode) {
      return;
    }
    event.preventDefault(); 
    setManualEntryMode(true);
    // Focus the active field input for manual entry
    setTimeout(() => {
      // could be buuggy
      const activeInput = document.getElementById(activeField);
      activeInput?.focus();
    }, 0);
  });

  useKeypress(["Escape"], (event) => {
    console.log("ESC Exiting manual entry mode");
    if (manualEntryMode) {
      event.preventDefault();
      setManualEntryMode(false);
    }
  });

  const getConfig = () => {
    switch (activeField) {
      case 'brand':
        return {
          title: 'Brand Suggestions',
          items: currentSuggestions?.brands || [],
          getKey: (item) => item.id,
          getText: (item) => item.brand_name,
          emptyText: 'No brand suggestions available',
          onSelect: (item) => {
            updateCurrentProduct({
              brand: item,
              brand_nm: item.brand_name,
              brand_name: item.brand_name || "",
              ext_brand_i: item.ext_id || null,
              sizes: item.sizes ? item.sizes.map(size => ({
                id: size.ext_id,
                title: size.size_c,
                max: size.max,
                min: size.min
              })) : [],
              is_adult_bev: item.is_adult_bev || false
            }, 'from_suggestion');
          }
        };

      case 'size_dimension_i':
        return {
          title: 'Size Dimension Suggestions',
          items: currentSuggestions?.dimensions || [],
          getKey: (item) => item.ext_id,
          getText: (item) => item.name,
          emptyText: 'No size dimension suggestions available',
          onSelect: (item) => {
            console.log("Selected size dimension:", item.ext_id);
            updateCurrentProduct({ size_dimension_i: item.ext_id })
          }
        };

      case 'ext_size_unit_i':
      case 'ext_size_unit_2_i':
        return {
          title: 'Unit Suggestions',
          items: currentSuggestions?.units || [],
          getKey: (item) => item.ext_id,
          getText: (item) => item.size_c,
          emptyText: 'No unit suggestions available',
          onSelect: (item) => {
            updateCurrentProduct({ [activeField]: item.ext_id });
          }
        };

      case 'size_1':
      case 'size_2':
      case 'size_3':
      case 'size_4':
        return {
          title: 'Size Suggestions',
          items: currentSuggestions?.sizes || [],
          getKey: (item) => item.ext_id,
          getText: (item) => item.size,
          emptyText: 'No size suggestions available',
          onSelect: (item) => {
            console.log("Selected size:", item.size, activeField);
            updateCurrentProduct({ [activeField]: item.size });
          }
        };
    }
  };

  const config = getConfig();

  if (!config || products.length === 0) {
    return (
      <div style={styles.noSuggestionsContainer}>
        <EmptyState text="No suggestions available for this element." />
      </div>
    );
  }

  return (
    <Card
      className="product-left-panel"
      style={styles.panel}
      styles={{
        body: styles.panelBody
      }
      }
    >
      <Title level={5} style={styles.title}>
        {config.title} {!manualEntryMode && <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>(Press 0 to enter manually)</Text>}
        {manualEntryMode && <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>(Manual Entry Mode - Press ESC to exit)</Text>}
      </Title>

      {
        config.items.length > 0 ? (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {config.items.map((item, index) => (
              <SuggestionItem
                key={config.getKey(item) || index}
                index={index}
                text={config.getText(item)}
                onClick={(event) => {
                  if (config.onSelect) {
                    config.onSelect(item);
                  } else {
                    updateCurrentProduct((product) => ({
                      ...product,
                      [activeField]: config.getKey(item)
                    }));
                  }
                  setTimeout(() => focusNextField("product-builder",), 0);
                }}
              />
            ))}
          </Space>
        ) : (
          <EmptyState text={config.emptyText} />
        )
      }
    </Card >
  );
}


export default SuggestionsPanel;